/**
 * APEX v8.8 — Player Profile
 *
 * Indicateurs joueur façon Porofessor :
 *   1. Tags comportementaux  — traits déduits de l'historique
 *   2. Playstyle radar       — 5 axes normalisés 0-100
 *   3. Forces / Faiblesses   — synthèse 2-3 + 2-3
 *
 * Tout est calculé depuis les games existantes — zéro API.
 */

function safeAvg(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function getValidGames(games) {
  return (Array.isArray(games) ? games : []).filter(g =>
    !(g.isRemake === true) &&
    !(typeof g.duration === 'number' && g.duration > 0 && g.duration < 5)
  );
}

/**
 * Radar playstyle — 5 axes normalisés 0-100.
 *   aggression  : kills + assists, morts (prise de risque)
 *   farming     : CS/min
 *   vision      : vision score
 *   survie      : inverse des morts/game
 *   carry       : part de damage / KDA
 */
export function computePlaystyleRadar(games) {
  const valid = getValidGames(games);
  if (valid.length < 5) return null;

  const avgKills   = safeAvg(valid.map(g => g.kills || 0));
  const avgAssists = safeAvg(valid.map(g => g.assists || 0));
  const avgDeaths  = safeAvg(valid.map(g => g.deaths || 0));
  const avgCs      = safeAvg(valid.filter(g => g.csPerMin).map(g => g.csPerMin));
  const avgVision  = safeAvg(valid.map(g => g.myStats?.visionScore || g.visionScore || 0));
  const avgKda     = safeAvg(valid.map(g => {
    const d = (g.deaths || 0) || 1;
    return ((g.kills || 0) + (g.assists || 0)) / d;
  }));

  // Normalisation sur des bornes réalistes Master+ EUW
  const norm = (val, min, max) => Math.max(0, Math.min(100, Math.round(((val - min) / (max - min)) * 100)));

  return {
    aggression: norm(avgKills + avgAssists * 0.5, 3, 14),
    farming:    norm(avgCs, 5.5, 9.5),
    vision:     norm(avgVision, 12, 40),
    survie:     norm(8 - avgDeaths, 2, 7),   // moins de morts = plus haut
    carry:      norm(avgKda, 1.5, 5),
  };
}

/**
 * Tags comportementaux — traits déduits de patterns.
 * Retourne un mix de tags positifs et neutres (pas que du négatif).
 */
export function computeBehaviorTags(games) {
  const valid = getValidGames(games);
  if (valid.length < 5) return [];

  const tags = [];

  const avgDeaths = safeAvg(valid.map(g => g.deaths || 0));
  const avgKills  = safeAvg(valid.map(g => g.kills || 0));
  const avgCs     = safeAvg(valid.filter(g => g.csPerMin).map(g => g.csPerMin));
  const avgVision = safeAvg(valid.map(g => g.myStats?.visionScore || g.visionScore || 0));
  const avgKda    = safeAvg(valid.map(g => ((g.kills||0)+(g.assists||0)) / ((g.deaths||0)||1)));

  // Greed deaths
  const greedTotal = valid.reduce((s, g) => s + (g.deathDetails||[]).filter(d => d?.type==='greed').length, 0);
  const totalDeaths = valid.reduce((s, g) => s + (g.deaths || 0), 0);
  const greedRatio = totalDeaths > 0 ? greedTotal / totalDeaths : 0;

  // Early aggression : kills tôt (pas de timestamp précis, on approxime via KDA et kills)
  if (avgKills >= 7) tags.push({ label: 'Agressif', type: 'neutral' });
  if (avgKills <= 3.5) tags.push({ label: 'Passif', type: 'neutral' });

  if (avgKda >= 3.5) tags.push({ label: 'Carry potential', type: 'positive' });
  if (avgDeaths <= 3.5) tags.push({ label: 'Discipliné', type: 'positive' });
  if (avgDeaths >= 6) tags.push({ label: 'Tilt prone', type: 'negative' });

  if (avgCs >= 8) tags.push({ label: 'Farm propre', type: 'positive' });
  if (avgCs > 0 && avgCs < 6) tags.push({ label: 'Farm à retravailler', type: 'negative' });

  if (avgVision >= 30) tags.push({ label: 'Map aware', type: 'positive' });
  if (avgVision > 0 && avgVision < 15) tags.push({ label: 'Vision faible', type: 'negative' });

  if (greedRatio >= 0.4) tags.push({ label: 'Greedy', type: 'negative' });
  if (greedRatio > 0 && greedRatio < 0.2 && totalDeaths > 10) tags.push({ label: 'Morts utiles', type: 'positive' });

  // Consistance : variance du résultat sur les 10 dernières
  const last10 = valid.slice(-10);
  if (last10.length >= 8) {
    const wins = last10.filter(g => g.result === 'W').length;
    if (wins >= 7) tags.push({ label: 'En forme', type: 'positive' });
    if (wins <= 3) tags.push({ label: 'Mauvaise passe', type: 'negative' });
  }

  return tags;
}

/**
 * Synthèse forces / faiblesses — 2-3 de chaque, formulées de façon actionnable.
 */
export function computeStrengthsWeaknesses(games) {
  const valid = getValidGames(games);
  if (valid.length < 5) return { strengths: [], weaknesses: [] };

  const avgDeaths = safeAvg(valid.map(g => g.deaths || 0));
  const avgKills  = safeAvg(valid.map(g => g.kills || 0));
  const avgCs     = safeAvg(valid.filter(g => g.csPerMin).map(g => g.csPerMin));
  const avgVision = safeAvg(valid.map(g => g.myStats?.visionScore || g.visionScore || 0));
  const avgKda    = safeAvg(valid.map(g => ((g.kills||0)+(g.assists||0)) / ((g.deaths||0)||1)));

  const candidates = [
    { metric: 'kda',    value: avgKda,    good: avgKda >= 3.2,    bad: avgKda < 2,
      strength: `KDA solide (${avgKda.toFixed(2)}) — tu transformes bien tes opportunités.`,
      weakness: `KDA faible (${avgKda.toFixed(2)}) — tu meurs trop par rapport à ton impact.` },
    { metric: 'deaths', value: avgDeaths, good: avgDeaths <= 3.5, bad: avgDeaths >= 5.5,
      strength: `Discipline de morts (${avgDeaths.toFixed(1)}/game) — tu survis aux moments clés.`,
      weakness: `Trop de morts (${avgDeaths.toFixed(1)}/game) — c'est ton axe #1 à corriger.` },
    { metric: 'cs',     value: avgCs,     good: avgCs >= 8,       bad: avgCs > 0 && avgCs < 6.5,
      strength: `Farm propre (${avgCs.toFixed(1)} cs/min) — tu maximises ton économie.`,
      weakness: `Farm insuffisant (${avgCs.toFixed(1)} cs/min) — tu retardes tes power spikes.` },
    { metric: 'vision', value: avgVision, good: avgVision >= 28,  bad: avgVision > 0 && avgVision < 16,
      strength: `Bonne vision (${Math.round(avgVision)}) — tu joues avec l'information.`,
      weakness: `Vision faible (${Math.round(avgVision)}) — tu te fais surprendre par manque de wards.` },
    { metric: 'kills',  value: avgKills,  good: avgKills >= 7,    bad: false,
      strength: `Présence offensive (${avgKills.toFixed(1)} kills/game) — tu pèses sur la map.`,
      weakness: null },
  ];

  const strengths = candidates.filter(c => c.good).map(c => c.strength).slice(0, 3);
  const weaknesses = candidates.filter(c => c.bad && c.weakness).map(c => c.weakness).slice(0, 3);

  return { strengths, weaknesses };
}
